import React, { useCallback, useContext, useEffect, useState } from 'react';
import CurrentUserContext from './CurrentUser.context';
import { User, USER_ROLES } from 'models/User';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import AuthContext from 'providers/Auth/Auth.context';
import { useTranslation } from 'react-i18next';
import credentialsService from 'services/credentialsService';
import { Loader } from 'ncoded-component-library';
import fleetManagerSocketService from 'services/socketService/fleetManagerSocketService';

const CurrentUserProvider: React.FC<{ children: React.ReactNode }> = (
  props,
) => {
  const { children } = props;
  const [user, setUser] = useState<User>();
  const [isUserFetching, setIsUserFetching] = useState(false);
  const { popError } = useContext(PopNotificationsContext);
  const { logoutRequest } = useContext(AuthContext);
  const { t } = useTranslation();

  const isAuthorised =
    user?.isPhoneNumberConfirmed &&
    !user?.hasTmpPassword &&
    !!credentialsService.token;

  const updateUser = useCallback((user: User) => {
    setUser(user);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setIsUserFetching(true);
        const { data } = await (credentialsService.token
          ? api.user.getCurrentUser()
          : api.authProcess.getCurrentUser());
        setUser(data);
      } catch (e) {
        popError(t('session-expired'));
        console.error(e);
        logoutRequest();
      } finally {
        setIsUserFetching(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAuthorised) return;
    fleetManagerSocketService.connect();

    return () => {
      fleetManagerSocketService.disconnect();
    };
  }, [isAuthorised]);

  const value = {
    user,
    updateUser,
    isAuthorised,
    isAdmin:
      user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.SUPER_ADMIN,
    isSuperAdmin: user?.role === USER_ROLES.SUPER_ADMIN,
  };

  return (
    <CurrentUserContext.Provider value={value}>
      {!user || isUserFetching ? <Loader /> : children}
    </CurrentUserContext.Provider>
  );
};

export default CurrentUserProvider;
